<script setup lang="ts">
import type { SupportAttachment } from '../../packages/client/src/types';
import { onBeforeUnmount, onMounted, ref, shallowRef, watch } from 'vue';

import ImageAttachmentPreview from './ImageAttachmentPreview.vue';

const props = defineProps<{
  attachment: SupportAttachment;
  load: () => Promise<Blob>;
  /** 身份或会话变化时，清理预览并作废在途请求。 */
  scope: unknown;
}>();

const container = ref<HTMLElement | null>(null);
const file = shallowRef<File | null>(null);
const failed = ref(false);
let visible = false;
let generation = 0;
let observer: IntersectionObserver | undefined;

async function load(): Promise<void> {
  const gate = ++generation;
  file.value = null;
  failed.value = false;
  const { name, mime } = props.attachment;
  try {
    const blob = await props.load();
    if (gate === generation) file.value = new File([blob], name, { type: mime });
  } catch {
    if (gate === generation) failed.value = true;
  }
}

function imageFailed(): void {
  file.value = null;
  failed.value = true;
}

watch(() => [props.scope, props.attachment.id, props.attachment.mime, props.attachment.name], () => {
  generation += 1;
  file.value = null;
  failed.value = false;
  if (visible) void load();
}, { flush: 'sync' });

onMounted(() => {
  // 仅在图片进入可视区附近时读取，避免历史消息一次下载全部原图。
  if (typeof IntersectionObserver === 'undefined') {
    visible = true;
    void load();
    return;
  }
  observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    observer?.disconnect();
    visible = true;
    void load();
  }, { rootMargin: '160px' });
  if (container.value) observer.observe(container.value);
});

onBeforeUnmount(() => {
  generation += 1;
  observer?.disconnect();
});
</script>

<template>
  <div ref="container" class="message-image" data-testid="message-image">
    <ImageAttachmentPreview v-if="file" :file="file" message @error="imageFailed" />
    <button v-else-if="failed" class="message-image__placeholder" type="button" @click.stop="load">
      图片加载失败，点击重试
    </button>
    <div v-else class="message-image__placeholder" role="status">图片加载中…</div>
  </div>
</template>

<style scoped>
.message-image { width: 240px; max-width: 100%; margin: 4px 0; }
.message-image__placeholder {
  box-sizing: border-box;
  display: grid;
  place-items: center;
  width: 100%;
  height: 160px;
  padding: 12px;
  border: 1px solid #dde2e8;
  border-radius: 8px;
  background: #f7f9fb;
  color: #64748b;
  font-size: 12px;
}
button.message-image__placeholder { cursor: pointer; }
</style>
